import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadHomepageBlogs } from '../utils/homepageBlogs';

const VISIBLE_ROWS = 5;
const ROW_HEIGHT_PX = 88;
const SCROLL_INTERVAL_MS = 3200;

export default function HomepageBlogsTicker() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await loadHomepageBlogs({ limit: 10 });
        if (!cancelled) {
          setBlogs(data.blogs || []);
        }
      } catch (err) {
        console.error('Failed to load homepage blogs', err);
        if (!cancelled) {
          setBlogs([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (reduceMotion || paused || blogs.length <= VISIBLE_ROWS) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setOffset((prev) => (prev + 1) % blogs.length);
    }, SCROLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [blogs.length, paused, reduceMotion]);

  const viewportHeight = ROW_HEIGHT_PX * VISIBLE_ROWS;
  const loopBlogs = blogs.length > 0 ? [...blogs, ...blogs] : [];

  return (
    <section className="relative px-6 py-16 max-w-full bg-white">
      <h2 className="text-3xl font-bold mb-8 text-center">From the Blog</h2>

      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="w-8 h-8 border-4 border-violet-800 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : blogs.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p className="text-lg font-medium">No blog posts yet.</p>
          <p className="text-sm mt-2">Check back soon for reading guides and classics.</p>
        </div>
      ) : (
        <div className="mx-auto w-3/4">
          <div
            className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            style={{ height: viewportHeight }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
          >
            <div
              ref={trackRef}
              className="transition-transform duration-700 ease-in-out"
              style={{
                transform: reduceMotion
                  ? 'translateY(0)'
                  : `translateY(-${offset * ROW_HEIGHT_PX}px)`,
              }}
            >
              {loopBlogs.map((blog, index) => (
                <Link
                  key={`${blog.slug}-${index}`}
                  to={`/blogs/${blog.slug}`}
                  className="flex flex-col justify-center border-b border-slate-100 px-5 hover:bg-violet-50/50 transition-colors"
                  style={{ height: ROW_HEIGHT_PX }}
                >
                  <span className="text-base font-semibold text-gray-900 line-clamp-1 md:text-lg">
                    {blog.title}
                  </span>
                  <span className="mt-1 text-sm text-gray-500 line-clamp-1 md:text-base">
                    {blog.excerpt || 'Read article'}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/blogs"
              className="inline-block text-violet-900 font-medium hover:underline transition"
            >
              See all blogs →
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
